import React, { useState, useEffect, useCallback, useRef } from 'react';
import { protectedAPI } from '../services/api';
import PlatesTable from './PlatesTable';
import {usePagination} from './PaginationHook.js';
import './Pagination.css'
import PaginationControls from './PaginationControls'
import {TableSearch, NumberTableRow} from './SearchFilter'
import { AuthProvider, useAuth } from '../context/AuthContext';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';

const accessRules = [
  {
    value: 'allowed',
    label: 'Разрешен',
  },
  {
    value: 'forbidden',
    label: 'Запрещён',
  }
];

export const SortingTableHeader = ({ onClick, column, columnName = ''  }) => {
  const [sortDir, setSortDir] = useState('asc');

  const handleSortClick = () => {
    if (sortDir == 'asc')
      setSortDir('desc');
    else if (sortDir == 'desc')
      setSortDir('asc')

    onClick({sortBy: column, sortDir: sortDir});
  };

  return (
    <th className='sortable' onClick={handleSortClick}>
      {columnName} <SwapVertIcon className='modern-icon'></SwapVertIcon>
    </th>
  );
};

const Plates = () => {
  const [addresses, setAddresses] = useState([]);
  const [platesByAddress, setPlatesByAddress] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  //const [error, setError] = useState('');
  const [filters, setFilters] = useState({});
  const [sortings, setSortings] = useState({});
  const { user, logout } = useAuth();
  const [openAddNumber, setOpenAddNumber] = useState(false);
  const [openAddNumberNotify, setOpenAddNumberNotify] = useState(false);
  const [selectedAccessRule, setSelectedAccessRule] = useState('allowed');
  const selectedAccessRuleRef = useRef(selectedAccessRule);
  const [selectedAddress, setSelectedAddress] = useState('');
  const selectedAddressRef = useRef(selectedAddress);
  const {
    data,
    pagination,
    loading,
    error,
    fetchData
  } = usePagination(protectedAPI.getPlatesByAddress, 1, 20, selectedAddressRef.current);

  const [number, setNumber] = useState('');
  const numberRef = useRef(number);
  const [numberError, setNumberError] = useState(false);

  const handlePageChange = (newPage) => {
    fetchData(newPage, pagination.pageSize, filters);
  };

  const handlePageSizeChange = (newPageSize) => {
    fetchData(1, newPageSize, filters);
  };

  const handleSearch = (searchFilters) => {
    setFilters(searchFilters);
    fetchData(1, pagination.pageSize, searchFilters);
  };

  const handleSortClick = (sortings) => {
    setSortings(sortings);
    fetchData(1, pagination.pageSize, filters, sortings);
  };

  const handleOpenAddNumber = () => {
    setOpenAddNumber(true);
  };

  const handleCloseAddNumber = () => {
    setOpenAddNumber(false);
  };

  const handleCloseAddNumberNotify = () => {
    setOpenAddNumberNotify(false);
  };

  const handleSelectAccessRuleChange = (e) => {
    const value = e.target.value;
    setSelectedAccessRule(value);
    selectedAccessRuleRef.current = value;
    console.log('Selected access rule value:', value);
  };

  const handleSelectAddressChange = (e) => {
    const value = e.target.value;
    setSelectedAddress(value);
    selectedAddressRef.current = value;
    console.log('Selected address value:', value);
  };

  const handleAddNumber = () => {
    fetchCreatePlate();
    setOpenAddNumber(false);
    setOpenAddNumberNotify(true);
  };

  const handleNumberChange = e => {
    setNumber(e.target.value);
    numberRef.current = e.target.value;
    if (e.target.validity.valid) {
      setNumberError(false);
    } else {
      setNumberError(true);
    }
  };
  
  useEffect(() => {
    setAddressesLoading(true);
    fetchAddress();
  }, []);

  const fetchCreatePlate = useCallback(async () => {
    try {
      const response = await protectedAPI.createPlate({
        user: user.id,
        address: selectedAddressRef.current,
        number_ru: document.getElementById('new-number').value,
        access_rule: selectedAccessRuleRef.current,
      });
      fetchData(1, pagination.pageSize, filters);
      //setLicensePlate(response.data);
    } catch (error) {
      //setError('Failed to fetch system state');
      alert("ERROR: " + error);
    } finally {
      //setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (addresses.length == 0) {
      return;
    }

    const fetchPlates = async () => {

      try {
        const platesPromises = addresses.map(async (addr) => {
          const response = await protectedAPI.getPlatesByAddress(addr.id);
          const plates = response.data;
          return {addressId: addr.id, plates };
        });

        const platesResult = await Promise.all(platesPromises);

        const platesMap = {};
        platesResult.forEach(({addressId, plates}) => {
          platesMap[addressId] = plates;
        });

        setPlatesByAddress(platesMap);
      } catch (error) {
        //setError('Failed to fetch plates by address');
      } finally {
        //setLoading(false);
      }
    }

    fetchPlates();

  }, [addresses.length]);

  const fetchAddress = async () => {
    try {
      const response = await protectedAPI.getAddresses();
      setAddresses(response.data);
      setSelectedAddress(response.data[0].id);
      selectedAddressRef.current = response.data[0].id;
    } catch (error) {
      //setError('Failed to fetch address');
    } finally {
      setAddressesLoading(false);
    }
  };

  //if (loading) return <div>Загрузка...</div>;

  if (error) {
    return (
      <div className="error-message">
        Ошибка загрузки данных: {error}
        <button onClick={() => fetchData(1, pagination.pageSize, filters)}>
          Повторить
        </button>
      </div>
    );
  }

  return (
    <div>
      <header>
        <h1>База номеров</h1>
      </header>
      <div className="table-container">
        <div className='search-line'>
          <TableSearch onSearch={handleSearch} />
          <Button variant="outlined" onClick={handleOpenAddNumber}>
            Добавить номер
          </Button>
        </div>

        <Dialog
          open={openAddNumber}
          onClose={handleCloseAddNumber}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Добавить номер
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Box
                component="form"
                sx={{ '& > :not(style)': { m: 1, width: '25ch' } }}
                noValidate
                autoComplete="off"
              >
                <TextField
                  id="new-address-controlled"
                  select
                  label="Адрес"
                  onChange={handleSelectAddressChange}
                  defaultValue={!addressesLoading && selectedAddress}
                >
                  {addresses.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  required
                  id="new-number"
                  label="Номер"
                  onChange={handleNumberChange}
                  error={numberError}
                  helperText={numberError && 'Ошибка! Пример: а001аа35'}
                  slotProps={{
                    htmlInput: {
                      pattern: "^[АВЕКМНОРСТУХавекмнорстух]{1}[0-9]{3}[АВЕКМНОРСТУХавекмнорстух]{2}[0-9]{2,3}"
                    },
                  }}
                />
                <TextField
                  id="new-rule-controlled"
                  select
                  label="Права доступа"
                  defaultValue={selectedAccessRule}
                  onChange={handleSelectAccessRuleChange}
                >
                  {accessRules.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddNumber}>Отменить</Button>
            <Button onClick={handleAddNumber} autoFocus disabled={numberError}>
              Добавить
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openAddNumberNotify} yjv 
          onClose={handleCloseAddNumberNotify}
          aria-labelledby="delete-number-notify-dialog-title"
        >
          <DialogTitle id="delete-number-notify-dialog-title">
            Номер {number} был успешно добавлен!
          </DialogTitle>
          <DialogActions>
            <Button onClick={handleCloseAddNumberNotify}>ОК</Button>
          </DialogActions>
        </Dialog>
        
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <SortingTableHeader
                  onClick={handleSortClick}
                  column='number_ru'
                  columnName='Номер'
                />
                <SortingTableHeader
                  onClick={handleSortClick}
                  column='access_rule'
                  columnName='Права доступа'
                />
                <SortingTableHeader
                  onClick={handleSortClick}
                  column='updated_at'
                  columnName='Дата изменения'
                />
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="loading-cell">
                    Загрузка...
                  </td>
                </tr>
              ) : (
                data.map((item) => (
                  <NumberTableRow key={item.id} data={item} />
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationControls
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Plates;